import { useEffect, useRef, useState } from "react";
import type { ByodLaptopConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

type Choice = ByodLaptopConfig["choices"][number];
type Stage =
  | "table"
  | "off"
  | "home"
  | "store"
  | "permissions"
  | "installing"
  | "installed"
  | "tracking"
  | "tracking_explain"
  | "run"
  | "result";

const KIND_ICON: Record<Choice["kind"], string> = {
  game: "\u{1F3AE}",
  update: "\u2699\uFE0F",
  email: "\u2709\uFE0F",
  screenshot: "\u{1F4F7}",
};

const KIND_APP: Record<Choice["kind"], string> = {
  game: "Game Store",
  update: "Settings",
  email: "Mail",
  screenshot: "Photos",
};

const COMPROMISE_STEPS = [
  "Launching FreeGold Puzzle…",
  "Reading Photos, Files and Contacts…",
  "Connecting to 185.203.116.42…",
  "Uploading Q3_client_pricing.pdf…",
];

/**
 * Operable BYOD tablet.
 *
 * The physical cutout is first shown lying on the café table. After Max picks it
 * up, the learner gets a clear task: either secure the tablet in Settings, or
 * open Game Store. The risky path is deliberately multi-step:
 * store listing -> Install -> permission request -> installed -> Open -> attacker
 * reads files and uploads the client deck. Merely opening an already-installed
 * app is never presented as the initial risk.
 */
export function ByodLaptop({
  config,
  onChoose,
  onStageChange,
}: {
  config: ByodLaptopConfig;
  onChoose: (choice: Choice) => void;
  onStageChange?: (stage: Stage) => void;
}) {
  const [stage, setStageRaw] = useState<Stage>("table");
  const setStage = (s: Stage) => {
    setStageRaw(s);
    onStageChange?.(s);
  };
  const [picked, setPicked] = useState<Choice | null>(null);
  const [step, setStep] = useState(0);
  const [runSteps, setRunSteps] = useState<string[]>([]);
  const [filesOpen, setFilesOpen] = useState(false);
  const [trackingChoice, setTrackingChoice] = useState<"allow" | "deny" | null>(null);
  const spoke = useRef(false);

  const gameChoice = config.choices.find((c) => c.kind === "game") ?? null;
  const settingsChoice = config.choices.find((c) => c.kind === "update") ?? null;

  useEffect(() => {
    if (stage !== "home" || spoke.current) return;
    spoke.current = true;
    void audio.speak(
      "Max wants a new game for the journey. He notices the tablet is 214 days behind on updates, " +
        "but downloads the game anyway.",
      { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
    );
  }, [stage, config]);

  useEffect(() => {
    onStageChange?.("table");
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = (choice: Choice) => {
    setStage("result");
    const line = choice.safe ? config.lenaSafe : config.lenaRisky;
    if (line) void audio.speak(line, { voice: "nova", lang: config.language ?? "en-US" });
  };

  const animate = (steps: string[], done: () => void, installing = false) => {
    setRunSteps(steps);
    setStep(0);
    setStage(installing ? "installing" : "run");
    let i = 0;
    const tick = () => {
      i += 1;
      setStep(i);
      if (i < steps.length) window.setTimeout(tick, 850);
      else window.setTimeout(done, 650);
    };
    window.setTimeout(tick, 450);
  };

  const openStore = () => {
    if (!gameChoice) return;
    setPicked(gameChoice);
    setStage("store");
  };

  const installGame = () => {
    if (!gameChoice) return;
    animate(
      ["Downloading FreeGold Puzzle…", "Installing application package…", "Installation complete."],
      () => setStage("installed"),
      true,
    );
  };

  const openInstalledGame = () => {
    if (!gameChoice) return;
    setStage("tracking");
  };

  const chooseTracking = (choice: "allow" | "deny") => {
    setTrackingChoice(choice);
    setStage("tracking_explain");
  };

  const afterTracking = () => {
    if (!gameChoice) return;
    animate(COMPROMISE_STEPS, () => finish(gameChoice));
  };

  if (stage === "table") {
    return (
      <button
        type="button"
        className="tab-table-stage tab-table-cta"
        onClick={() => {
          audio.click();
          setStage("off");
        }}
      >
        <span>{tx("Max's personal tablet · Pick it up")}</span>
      </button>
    );
  }

  return (
    <div className="tab-wrap" aria-label={tx("Personal tablet")}>
      <div className="tab-device">
        <span className="tab-cam" aria-hidden />

        {stage === "off" && (
          <button
            type="button"
            className="tab-lock cf-pulse-target"
            onClick={() => {
              audio.powerOn();
              setStage("home");
            }}
          >
            <span className="tab-lock-time">{config.clock ?? "10:42"}</span>
            <span className="tab-lock-date">{tx("Personal tablet")}</span>
            <span className="tab-lock-hint">{tx("Tap to unlock")}</span>
          </button>
        )}

        {stage === "home" && (
          <div className="tab-home">
            <div className="tab-status">
              <span>{config.clock ?? "10:42"}</span>
              <span className="tab-status-icons" aria-hidden>
                {"\u{1F4F6}"} {"\u{1F50B}"}
              </span>
            </div>
            {/* 2026-07-17: restyled as a real OS notification card (icon
                badge + fixed "System Update" title + the author's detail
                line) instead of a bare warning emoji + run-on sentence.
                The old separate "Your turn: click Game Store..." instruction
                box duplicated the top-right guide tooltip word-for-word
                (TABLET_STAGE_GUIDE.home in App.tsx already says exactly
                this) -- removed so the home screen reads like a real
                tablet desktop instead of a UI mockup with instructions
                printed on it, matching the cafecharge/publicpc cleanup. */}
            <div className="tab-nag" role="status">
              <span className="tab-nag-ico" aria-hidden>{"\u2699\uFE0F"}</span>
              <div className="tab-nag-body">
                <strong>{tx("System Update")}</strong>
                <span>{config.osBanner ?? tx("23 security patches pending — last checked 214 days ago")}</span>
              </div>
            </div>
            <div className="tab-grid">
              <button type="button" className="tab-app" onClick={() => setFilesOpen(true)}>
                <span className="tab-app-ico files" aria-hidden>{"\u{1F4C1}"}</span>
                <span className="tab-app-label">{tx("Files")}</span>
              </button>
              {gameChoice && (
                <button type="button" className="tab-app cf-pulse-target" onClick={openStore}>
                  <span className="tab-app-ico k-game" aria-hidden>{KIND_ICON.game}</span>
                  <span className="tab-app-label">{tx("Game Store")}</span>
                  <span className="tab-app-hint">{tx("Click here")}</span>
                </button>
              )}
              {settingsChoice && (
                // 2026-07-14: Settings is intentionally LOCKED for this beat.
                // The task copy above already tells the learner to open Game
                // Store — a live "one-tap fix" Settings button let curious
                // learners dodge the entire tracking/malware sequence this
                // beat exists to teach. It stays visible (so the correct
                // real-world move is still shown) but isn't clickable here.
                <button type="button" className="tab-app safe locked" disabled aria-disabled="true">
                  <span className="tab-app-ico k-update" aria-hidden>{"\u{1F512}"}</span>
                  <span className="tab-app-label">{tx("Settings")}</span>
                  <span className="tab-app-hint">{tx("Locked for this drill")}</span>
                </button>
              )}
            </div>
            <div className="tab-homebar" aria-hidden />

            {filesOpen && (
              <div className="tab-modal">
                <div className="tab-modal-card">
                  <div className="tab-modal-file">
                    <span aria-hidden>{"\u{1F4C4}"}</span> Q3_client_pricing.pdf
                  </div>
                  <div className="tab-blocked">
                    <span aria-hidden>{"\u{1F512}"}</span>
                    {tx("Blocked—this file needs a managed work profile. Secure the tablet in Settings before opening company data.")}
                  </div>
                  <button type="button" className="btn btn-ghost" onClick={() => setFilesOpen(false)}>
                    {tx("Close")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {stage === "store" && picked && (
          <div className="tab-store">
            <div className="tab-store-head">
              <button type="button" onClick={() => setStage("home")} aria-label={tx("Back")}>{"\u2039"}</button>
              {tx("Game Store")}
            </div>
            <div className="tab-store-app">
              <span className="tab-store-icon" aria-hidden>{"\u{1F3AE}"}</span>
              <div>
                <strong>{tx("FreeGold Puzzle")}</strong>
                <small>{tx("Unknown publisher · New release")}</small>
                <span className="tab-store-rating">{"\u2605\u2605\u2605\u2605\u2606"} {tx("· 14 downloads")}</span>
              </div>
            </div>
            <p>
              {tx("“The game everyone's talking about.” This publisher has no verified identity and the app has almost no review history.")}
            </p>
            <button type="button" className="btn tab-install cf-pulse-target" onClick={() => setStage("permissions")}>
              {tx("Install")}
            </button>
            <p className="tab-walkthrough-note">
              {txr("This is a guided walkthrough, not a judgment on you — tap <0>Install</0> to see exactly what a shady app asks for next.", {}, [<strong />])}{" "}
              <button type="button" className="tab-skip-link" onClick={installGame}>
                {tx("Skip ahead & show me anyway")}
              </button>
            </p>
          </div>
        )}

        {stage === "permissions" && (
          <div className="tab-permissions">
            <div className="tab-permissions-kicker">{tx("App store permission request")}</div>
            <h3>{tx("Allow FreeGold Puzzle to access:")}</h3>
            <ul>
              <li>{"\u{1F5BC}\uFE0F"} {tx("All photos and videos")}</li>
              <li>{"\u{1F4C1}"} {tx("All files and documents")}</li>
              <li>{"\u{1F465}"} {tx("Contacts")}</li>
            </ul>
            <p>{tx("A puzzle game does not need any of these permissions.")}</p>
            <div>
              <button type="button" className="btn btn-ghost" onClick={() => setStage("store")}>
                {tx("Cancel")}
              </button>
              <button type="button" className="btn btn-danger cf-pulse-target" onClick={installGame}>
                {tx("Allow & install")}
              </button>
            </div>
            <p className="tab-walkthrough-note">
              {txr("In real life, <0>Cancel</0> is the right call — that's why we let you make it. This training needs to show you what happens if Max doesn't, so hitting Cancel sends you back to the store rather than ending the drill.", {}, [<strong />])}{" "}
              <button type="button" className="tab-skip-link" onClick={installGame}>
                {tx("Skip ahead & show me anyway")}
              </button>
            </p>
          </div>
        )}

        {stage === "tracking" && (
          // Explanation rewritten 2026-07-13: the old copy dismissed this as
          // "not about the malicious access, damage already done" without
          // ever explaining what allowing tracking itself actually exposes.
          <div className="tab-tracking">
            <div className="tab-tracking-icon" aria-hidden>{"\u{1F3AE}"}</div>
            <h3>{tx("\"FreeGold Puzzle\" would like permission to track your activity across other apps and websites")}</h3>
            <p>
              {txr("This controls the device's advertising ID. <0>Allow</0> lets FreeGold Puzzle link everything it sees with other apps and sites Max uses, building a profile of his habits, interests, and location over time — which gets sold to data brokers and ad networks, often far beyond the app's own servers.", {}, [<strong />])}
            </p>
            <div className="tab-tracking-actions">
              <button type="button" className="tab-tracking-allow cf-pulse-target" onClick={() => chooseTracking("allow")}>
                {tx("Allow")}
              </button>
              <button type="button" className="tab-tracking-deny" onClick={() => chooseTracking("deny")}>
                {tx("Ask App Not to Track")}
              </button>
            </div>
            <div className="tab-tracking-note">
              {tx("\"Ask App Not to Track\" blocks that cross-app profile — though an app already this shady may ignore it. Either way, this is a SEPARATE, second exposure on top of the photos, files, and contacts access it already grabbed a moment ago.")}
            </div>
          </div>
        )}

        {stage === "tracking_explain" && (
          <div className="tab-tracking-explain">
            <div className="tab-tracking-explain-head">
              <span aria-hidden>{trackingChoice === "allow" ? "\u{1F440}" : "\u{1F6D1}"}</span>
              <h3>
                {trackingChoice === "allow"
                  ? tx("Allowed — here is what that actually hands over")
                  : tx("Blocked the tracking request — but two other problems remain")}
              </h3>
            </div>
            <div className="tab-tracking-explain-sec">
              <strong>{tx("Tracking, unpacked")}</strong>
              <p>
                {trackingChoice === "allow"
                  ? tx("Allow links the tablet's advertising ID across every app and site Max uses on it. FreeGold Puzzle — and anyone it resells the feed to — can now build a profile of his habits, interests, and location over time. That profile is routinely sold on to data brokers and ad networks, who combine it with other leaked or purchased data to re-identify people, score them for credit/insurance, or target them for scams.")
                  : tx("“Ask App Not to Track” blocks the official cross-app advertising-ID link on this device — but it is a request, not a lock: a shady, unverified publisher can ignore it, and it does nothing to stop device fingerprinting, IP-based location, or the photos/files/contacts access already granted a moment ago.")}
              </p>
            </div>
            <div className="tab-tracking-explain-sec">
              <strong>{tx("Unpatched device, unpacked")}</strong>
              <p>
                {tx("This tablet is 214 days behind on security patches. Every one of those monthly updates closed publicly known vulnerabilities — attackers don't need to discover a new flaw, they just need a device that never installed last year's fixes. Because it also has no managed work profile, there is no isolation between work and personal data and no way to remotely wipe it if it's compromised or lost.")}
              </p>
            </div>
            <button type="button" className="btn btn-danger cf-pulse-target" onClick={afterTracking}>
              {tx("Continue")}
            </button>
          </div>
        )}

        {stage === "installed" && picked && (
          <div className="tab-installed">
            <span className="tab-store-icon" aria-hidden>{"\u{1F3AE}"}</span>
            <h3>{tx("FreeGold Puzzle installed")}</h3>
            <p>{tx("The app now has access to photos, files and contacts.")}</p>
            <button type="button" className="btn btn-danger cf-pulse-target" onClick={openInstalledGame}>
              {tx("Open")}
            </button>
          </div>
        )}

        {(stage === "run" || stage === "installing") && picked && (
          <div className={`tab-run ${picked.safe ? "safe" : "bad"}`}>
            <div className="tab-run-app">
              {stage === "installing" ? tx("⬇️ Installing new app") : `${KIND_ICON[picked.kind]} ${KIND_APP[picked.kind]}`}
            </div>
            <ul className="tab-run-steps">
              {runSteps.slice(0, step).map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
            <div className="tab-run-spinner" aria-hidden />
          </div>
        )}

        {stage === "result" && picked && (
          <div className={`tab-result ${picked.result.bad ? "bad" : "good"}`}>
            <div className="tab-result-ico" aria-hidden>
              {picked.result.bad ? "\u2620\uFE0F" : "\u2714\uFE0F"}
            </div>
            <h3>{picked.result.title}</h3>
            <p>{picked.result.body}</p>
            {picked.result.bad && (
              <div className="tab-official-note">
                {txr("<0>Why did this pass the store listing?</0> Being on an official app store is a filter, not a guarantee — reviewers scan for KNOWN malware signatures, but a new, unverified publisher with almost no history can still slip a first-seen threat past that check. The broad photo/file/contacts permission it asked for was the real warning sign, not the store it came from.", {}, [<strong />])}
              </div>
            )}
            <button type="button" className="btn cf-pulse-target" onClick={() => onChoose(picked)}>
              {tx("Continue")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
